<script lang="ts">
  import GridItem from "$lib/atoms/GridItem.svelte";
  import { renderRichText } from "@storyblok/svelte";
  import { createEventDispatcher, onMount, tick } from "svelte";
  import type { ColorMapColor, ToastBarBlok } from "$types/components";
  import GridContainer from "./GridContainer.svelte";
  import { page } from "$stores";

  export let blok: ToastBarBlok;

  const dispatch = createEventDispatcher();
  const colorMap: Record<ColorMapColor, string> = {
    black: "rgb(var(--color-black))",
    white: "rgb(var(--color-white))",
    charcoal: "#242021",
    heart: "#f73b30",
    forest: "#04462F",
  };

  let toastBar: HTMLElement;
  let close: HTMLElement;
  let height: number;
  let hide = true;
  let cookieName = "toastBar";
  let title = blok[0]?.title || "";
  let renderedRichText = blok[0]?.content
    ? renderRichText(blok[0].content)
    : "";
  let backgroundColor = blok[0]?.background_color || "black";
  let textColor = blok[0]?.text_color || "white";

  $: containsUkrainianText = () => {
    return /[а-яА-ЯЁё]/.test(renderedRichText);
  };

  async function mountFunc() {
    await tick();
    if (getCookie(cookieName)) {
      title = "";
      renderedRichText = "";
    }
    setToastBarHeight();
  }

  onMount(() => {
    page.subscribe((newPage) => {
      if (newPage && newPage.toast_bar && newPage.toast_bar?.length > 0) {
        cookieName = `toastBar${newPage._uid}`;
        title = newPage.toast_bar[0].title;
        renderedRichText = renderRichText(newPage.toast_bar[0].content);
        backgroundColor = newPage.toast_bar[0].background_color || "black";
        textColor = newPage.toast_bar[0].text_color || "white";
      } else {
        if (blok.length > 0) {
          title = blok[0].title;
          renderedRichText = renderRichText(blok[0].content);
          backgroundColor = blok[0].background_color || "black";
          textColor = blok[0].text_color || "white";
          cookieName = `toastBar`;
        } else {
          title = "";
          renderedRichText = "";
        }
      }
      mountFunc();
    });
  });

  function onCloseClick() {
    hide = true;
    height = 0;
    dispatch("toastBarResize", { height });

    if (window._iub.csEnabled) {
      setCookie(cookieName, "hidden", 7);
    }
  }

  async function setToastBarHeight() {
    if (title.length || renderedRichText.length) {
      hide = false;
      await tick();
      height = toastBar.getBoundingClientRect().height;
    } else {
      hide = true;
      height = 0;
    }
    dispatch("toastBarResize", { height });
  }

  function setCookie(name: string, value: string, daysToLive: number) {
    let cookie = name + "=" + encodeURIComponent(value);
    if (typeof daysToLive === "number") {
      cookie += "; max-age=" + daysToLive * 24 * 60 * 60;
      document.cookie = cookie;
    }
  }

  function getCookie(name: string) {
    const cookieArr = document.cookie.split(";");
    for (var i = 0; i < cookieArr.length; i++) {
      const cookiePair = cookieArr[i].split("=");
      if (name == cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }
</script>

<svelte:window on:resize={setToastBarHeight} />

<div
  class="toast-bar type:body-small"
  bind:this={toastBar}
  class:hidden={hide}
  class:shown={!hide}
  style:background-color={colorMap[backgroundColor]}
  style:color={colorMap[textColor]}
>
  <GridContainer alignItems="end">
    <GridItem col={12} colMd={12}>
      <div class="wrapper">
        <div>
          <span>{title}</span>
        </div>
        <div
          class="content {containsUkrainianText() ? '' : ''}"
          style={containsUkrainianText() ? "font-family:Inter;" : ""}
        >
          {@html renderedRichText}
        </div>
        <button class="close" bind:this={close} on:click={onCloseClick}>
          <img src="/svg/close-white.svg" alt="close" />
        </button>
      </div>
    </GridItem>
  </GridContainer>
</div>

<style lang="scss">
  .toast-bar {
    background-color: rgb(var(--color-black));
    color: rgb(var(--color-white));
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1000;
    opacity: 0;

    @include breakpoint("small") {
      padding: 0 20px;
    }

    &.shown {
      opacity: 1;
    }

    &.hidden {
      display: none;
    }
  }

  .wrapper {
    display: flex;
    min-height: fluid(38px, 76px);
    align-items: center;

    @include breakpoint("small") {
      min-height: fluid(38px, 38px);
    }
  }

  .content {
    flex: 1;
    text-align: center;
    padding: 0 20px;

    :global(a) {
      color: inherit;
    }
  }

  .close {
    background: transparent;
    border: 0;
    cursor: pointer;
  }
</style>
